import React, { useEffect, useRef, useState } from 'react';
import { SlashLink as Link } from './SlashLink';
import ArrowIcon from './ArrowIcon';
import { phiTrack } from '../utils/phiTrack';
import { forwardToGa } from '../utils/ga';

/** Seconds the homepage clock runs before it stops and hands over to the CTA. */
export const TICK_SECONDS = 10;

export function formatClock(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function track(eventName) {
  phiTrack(eventName, { placement: 'home_hero' });
  forwardToGa(eventName, { placement: 'home_hero' });
}

/**
 * "2:15:00 on the clock" on the homepage hero card. Once the card is at least half visible it
 * counts down for real for TICK_SECONDS (pausing while off-screen), then stops and pulses the
 * "Start the clock" link. Reduced motion or no IntersectionObserver: the static session length.
 */
export default function HeroSessionClock({ minutes, ctaHref }) {
  const total = minutes * 60;
  const rootRef = useRef(null);
  const seenRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion() || typeof window.IntersectionObserver !== 'function' || !rootRef.current) return undefined;
    setAnimate(true);
    const observer = new window.IntersectionObserver(
      ([entry]) => setVisible(Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.5)),
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  const done = elapsed >= TICK_SECONDS;

  useEffect(() => {
    if (!animate || !visible || done) return undefined;
    if (!seenRef.current) {
      seenRef.current = true;
      track('home_clock_seen');
    }
    const id = window.setInterval(() => setElapsed((e) => Math.min(TICK_SECONDS, e + 1)), 1000);
    return () => window.clearInterval(id);
  }, [animate, visible, done]);

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const spoken = `${hours} hour${hours === 1 ? '' : 's'}${mins ? ` ${mins} minutes` : ''}`;

  return (
    <div ref={rootRef} className="hero-spec-clock-wrap">
      <p className="hero-spec-clock">
        <strong aria-hidden="true">{formatClock(total - elapsed)}</strong>
        <span className="visually-hidden">{spoken}</span> on the clock
      </p>
      <Link
        className={`hero-spec-cta${animate && done ? ' is-pulsing' : ''}`}
        to={ctaHref}
        onClick={() => track('home_clock_cta_click')}
      >
        Start the clock
        <ArrowIcon />
      </Link>
    </div>
  );
}
